import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const sandDune: TemplateRegistration = {
  meta: {
    id: 'sand-dune',
    name: 'Sand Dune',
    category: 'Minimal',
    description: 'Warm sandy tones with rounded sections and soft curves',
    previewImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Quicksand', 'Mulish']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const pullQuote = truncateAtSentence(safeAbout, 120);
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <h2 class="section-title">About Us</h2>
          <div class="about-split">
            <div class="about-quote">&ldquo;${pullQuote}&rdquo;</div>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceRows = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-row">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <div class="service-body">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-alt">${serviceRows}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card sand-card">
            <p class="testimonial-body">&ldquo;${tContent}&rdquo;</p>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item sand-card">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row sand-card">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <div class="sand-card">
            <ul class="hours-list">${hoursList}</ul>
          </div>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-centered sand-card">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #3E2723; --bg: #FAF6F0; --bg-alt: #F0E8DC; --accent: #D2B48C;
      --text: #3E2723; --text-muted: #7C6354;
      --font-heading: 'Quicksand', sans-serif; --font-body: 'Mulish', sans-serif;
      --radius: 24px; --radius-sm: 16px;
      --shadow-sm: 0 2px 8px rgba(62,39,35,.06); --shadow-md: 0 6px 20px rgba(62,39,35,.1);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; }
    .container { max-width: 1060px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* SAND CARD */
    .sand-card { background: #fff; padding: 32px; border-radius: var(--radius); box-shadow: var(--shadow-sm); }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(250,246,240,.95); backdrop-filter: blur(10px); border-bottom: 1px solid #E8DFD2; }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1060px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.4rem; color: var(--text); font-weight: 700; }
    .nav-links { display: flex; gap: 26px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; font-weight: 600; letter-spacing: 0.5px; }
    .nav-links a:hover { color: var(--primary); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; border-radius: 3px; }

    /* HERO - circular focus */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; overflow: hidden; background: var(--bg); }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .12; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(250,246,240,.92), var(--bg)); }
    .hero-content { position: relative; z-index: 2; padding: 140px 24px 100px; max-width: 1060px; margin: 0 auto; width: 100%; text-align: center; display: flex; flex-direction: column; align-items: center; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.4rem, 6vw, 4rem); line-height: 1.15; margin-bottom: 32px; font-weight: 700; color: var(--text); }
    .hero-circle { width: 280px; height: 280px; border-radius: 50%; overflow: hidden; margin-bottom: 32px; box-shadow: var(--shadow-md); border: 6px solid var(--accent); flex-shrink: 0; }
    .hero-circle img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .hero-circle .hero-no-img { width: 100%; height: 100%; background: linear-gradient(135deg, var(--accent), var(--bg-alt)); display: flex; align-items: center; justify-content: center; font-family: var(--font-heading); font-size: 1.2rem; color: var(--primary); }
    .hero-sub { font-size: 1.05rem; color: var(--text-muted); max-width: 540px; margin-bottom: 36px; line-height: 1.8; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--accent); color: var(--primary); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; border: none; border-radius: 50px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: var(--shadow-md); }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(210,180,140,.3); opacity: 1; color: var(--primary); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.6rem, 3.5vw, 2.4rem); font-weight: 700; margin-bottom: 40px; color: var(--text); text-align: center; }

    /* ABOUT - quote + text side-by-side */
    .about-split { display: grid; grid-template-columns: 1fr 1.5fr; gap: 40px; align-items: start; }
    .about-quote { font-family: var(--font-heading); font-size: 1.4rem; font-weight: 700; color: var(--primary); line-height: 1.5; border-left: 4px solid var(--accent); padding-left: 20px; font-style: italic; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.9; }

    /* SERVICES - alternating rows (icon left/right) */
    .services-alt { display: flex; flex-direction: column; gap: 24px; max-width: 800px; margin: 0 auto; }
    .service-row { display: flex; align-items: center; gap: 24px; padding: 28px; background: #fff; border-radius: var(--radius); box-shadow: var(--shadow-sm); transition: transform .3s, box-shadow .3s; }
    .service-row:nth-child(even) { flex-direction: row-reverse; }
    .service-row:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
    .service-icon { flex-shrink: 0; width: 56px; height: 56px; background: var(--bg-alt); border-radius: 50%; display: flex; align-items: center; justify-content: center; }
    .icon-char { font-size: 28px; line-height: 1; color: var(--accent); }
    .service-body { flex: 1; }
    .service-body h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 8px; color: var(--text); }
    .service-body p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; font-weight: 700; font-size: 1.05rem; color: var(--primary); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border-radius: var(--radius-sm); box-shadow: var(--shadow-sm); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS - 2-col grid */
    .testimonials-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
    .testimonial-card { text-align: left; transition: transform .3s; }
    .testimonial-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
    .testimonial-body { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.8; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: .95rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #DDD4C8; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; gap: 16px; max-width: 750px; margin: 0 auto; }
    .faq-q { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 700; margin-bottom: 8px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { display: flex; flex-direction: column; gap: 16px; max-width: 750px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 700; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.3rem; color: var(--primary); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #E8DFD2; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; border-radius: 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--primary); font-size: 1.2rem; }
    .review-text { color: var(--primary); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: var(--bg); padding: 20px 0; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: center; }
    .promo-badge { background: var(--primary); color: var(--bg); font-family: var(--font-heading); padding: 8px 24px; font-size: 1rem; font-weight: 700; border-radius: 50px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - centered card */
    .contact-centered { max-width: 560px; margin: 0 auto; }
    .contact-info { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; align-items: center; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 22px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; font-size: .85rem; color: var(--primary); }
    .contact-form { display: flex; flex-direction: column; gap: 14px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 1px solid #E8DFD2; border-radius: 12px; color: var(--text); padding: 14px 18px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 14px; background: var(--accent); color: var(--primary); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; border: none; border-radius: 50px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(210,180,140,.3); }

    /* FOOTER */
    .footer { background: var(--primary); padding: 40px 0; text-align: center; }
    .footer p { color: #A89080; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(250,246,240,.98); border-bottom: 1px solid #E8DFD2; padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .about-split { grid-template-columns: 1fr; }
      .service-row:nth-child(even) { flex-direction: row; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .testimonials-grid { grid-template-columns: 1fr; }
      .hero-circle { width: 220px; height: 220px; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 110px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <h1>${safeHeadline}</h1>
      <div class="hero-circle">
        ${heroImage ? `<img src="${escapeAttr(heroImage)}" alt="${escapeAttr(safeName)}">` : `<div class="hero-no-img">${safeName}</div>`}
      </div>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
